import { Flex, Text } from "@chakra-ui/layout";
import { useAnnouncements } from "../hooks/announcements.hooks";
import { useMemo, useRef, useState } from "react";
import {
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	useReactTable,
	type ColumnDef,
} from "@tanstack/react-table";
import {
	type Announcement,
	AnnouncementType,
} from "../types/announcement.types";
import { Box, useDisclosure } from "@chakra-ui/react";
import SimpleModal from "../components/modals/SimpleModal";
import AnnouncementForm, {
	type AnnouncementFormHandler,
} from "../components/forms/AnnouncementForm";
import { useMutation, useQueryClient } from "react-query";
import { updateAnnouncement } from "../mutations/announcements.mutations";
import ClientSideTable from "../components/table/ClientSideTable";
import i18n from "../i18n";
import { File } from "lucide-react";
import { useSimpleToast } from "../hooks/toast.hooks";

const SettingsPageAnnouncementPanel = () => {
	const data = useAnnouncements(1, 1000);
	const toast = useSimpleToast();
	const announcements = useMemo(() => {
		if (data == null) return [];
		const findAnnouncementForType = (
			type: AnnouncementType,
		): Partial<Announcement> => {
			const found = data.data.find((a) => a.type === type);

			if (found == null) {
				return {
					type,
				};
			}
			return found;
		};

		return [
			findAnnouncementForType(AnnouncementType.GLOBAL),
			findAnnouncementForType(AnnouncementType.COMMAND),
			findAnnouncementForType(AnnouncementType.NAV),
		];
	}, [data]);
	const disclosure = useDisclosure();
	const columns = useMemo<ColumnDef<Partial<Announcement>>[]>(
		() => [
			{
				cell: ({ row }) => (
					<Box
						className="flex gap-2 items-center"
						onClick={() => {
							setAnnouncement(row.original);
							disclosure.onOpen();
						}}
					>
						<File />
						<Text>{i18n.t(`announcements.type.${row.original.type}`)}</Text>
					</Box>
				),
				id: "content",
				header: "Contenu",
				sortDescFirst: true,
			},
		],
		[disclosure],
	);
	const table = useReactTable({
		data: announcements,
		columns: columns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
	});
	const queryClient = useQueryClient();
	const ref = useRef<AnnouncementFormHandler | null>(null);
	const handleClose = () => {
		disclosure.onClose();
		setAnnouncement(null);
	};
	const updateAnnouncementMutation = useMutation({
		mutationFn: updateAnnouncement,
		onSuccess: () => {
			handleClose();
			queryClient.invalidateQueries("announcements");
			toast.success("Information", "L'annonce a été mise à jour");
		},
		onError: () => {
			handleClose();
			toast.error("Information", "Erreur lors de la mise à jour de l'annonce");
		},
	});
	const [announcement, setAnnouncement] =
		useState<Partial<Announcement> | null>(null);

	return (
		<Flex direction="column">
			<Flex direction="column" justify="space-between" gap={4}>
				<Text fontSize="18px" fontWeight={600} className="text-gray-900">
					Contenus
				</Text>
				<ClientSideTable table={table} noSearch noPagination />
			</Flex>

			{announcement && (
				<SimpleModal
					title={i18n.t(`announcements.type.${announcement.type}`)}
					acceptText={"Enregistrer"}
					isOpen={disclosure.isOpen}
					onClose={handleClose}
					onClick={async () => {
						if (updateAnnouncementMutation.isLoading) return;
						if (ref.current === null) return;
						const valid = await ref.current.valid();
						if (valid === false) return;
						const announcementUpdatedFields = ref.current.values();
						updateAnnouncementMutation.mutate({
							...announcement,
							...announcementUpdatedFields,
						});
					}}
				>
					<AnnouncementForm ref={ref} value={announcement ?? undefined} />
				</SimpleModal>
			)}
		</Flex>
	);
};

export default SettingsPageAnnouncementPanel;
